import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import type { GetMeResponseDTO, User } from '@/types';
import { api } from '@/store/api';
import { getAccountInfo, getMsalInstance, loginRequest } from '@/services/msal';

function isUnauthorized(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    'status' in error &&
    (error as { status: unknown }).status === 401
  );
}

interface AuthState {
  isAuthenticated: boolean;
  user: (User & { isManager?: boolean }) | null;
  token: string | null;
  loading: boolean;
}

// Check if token exists in localStorage
const token = localStorage.getItem('token');
const initialState: AuthState = {
  isAuthenticated: !!token,
  user: token ? JSON.parse(localStorage.getItem('user') || 'null') : null,
  token,
  loading: false,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setCredentials: (
      state,
      action: PayloadAction<{ user: User; token: string }>
    ) => {
      state.user = action.payload.user;
      state.token = action.payload.token;
      state.isAuthenticated = true;
      localStorage.setItem('token', action.payload.token);
      localStorage.setItem('user', JSON.stringify(action.payload.user));
      const tabId = sessionStorage.getItem('tabId');
      if (tabId) {
        localStorage.setItem('activeTabId', tabId);
        localStorage.setItem('activeTabHeartbeat', Date.now().toString());
      }
    },
    logout: state => {
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      const tabId = sessionStorage.getItem('tabId');
      if (localStorage.getItem('activeTabId') === tabId) {
        localStorage.removeItem('activeTabId');
        localStorage.removeItem('activeTabHeartbeat');
      }
    },

    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setUser: (state, action: PayloadAction<GetMeResponseDTO>) => {
      state.user = {
        ...(state.user as User),
        isManager: action.payload.isManager,
        jobTitle: action.payload.jobTitle,
      };
    },
  },
});

/** Fetches /auth/me and merges into auth state. Use when rehydrated (credentials already in state). */
export const fetchUserProfile = createAsyncThunk(
  'auth/fetchUserProfile',
  async (_, { dispatch, rejectWithValue }) => {
    const getMeOptions = {
      subscribe: false as const,
      forceRefetch: true as const,
    };

    try {
      const me = await dispatch(
        api.endpoints.getMe.initiate(undefined, getMeOptions)
      ).unwrap();
      dispatch(setUser(me));
    } catch (err) {
      if (!isUnauthorized(err)) {
        return rejectWithValue(
          err instanceof Error ? err.message : 'fetchUserProfile failed'
        );
      }

      dispatch(logout());

      const instance = await getMsalInstance();
      if (!instance) {
        return rejectWithValue('unauthorized');
      }

      try {
        const response = await instance.loginPopup(loginRequest);
        if (!response.account) {
          return rejectWithValue('unauthorized');
        }
        const accountInfo = getAccountInfo(response.account);
        if (!accountInfo || !response.idToken) {
          return rejectWithValue('unauthorized');
        }
        dispatch(
          setCredentials({ user: accountInfo, token: response.idToken })
        );
        const me = await dispatch(
          api.endpoints.getMe.initiate(undefined, getMeOptions)
        ).unwrap();
        dispatch(setUser(me));
      } catch {
        return rejectWithValue('unauthorized');
      }
    }
  }
);

/** Sets credentials and fetches full user profile from /auth/me. Use for new logins / SSO. */
export const prepareUser = createAsyncThunk(
  'auth/prepareUser',
  async (payload: { user: User; token: string }, { dispatch }) => {
    dispatch(setCredentials(payload));
    await dispatch(fetchUserProfile());
  }
);

export const { setCredentials, logout, setLoading, setUser } =
  authSlice.actions;
export default authSlice.reducer;
